import { useQuery } from "@apollo/client";
import { LocationGenerics } from "router/location";
import {
  dispatchModal,
  wrapClick,
} from "utils";
import {
  RATServiceOrderDetails,
  RATServiceOrderResolutionDetails,
  ServiceOrderHistoryView,
} from "components/cards";
import { Modal, Tabs } from "components/layouts";
import { useState } from "react";
import { useSearch } from "react-location";
import { GET_SERVICE_ORDER } from "./apollo";

const orderTabs = [
  {
    name: "Order Details",
    href: "OrderDetails",
    activeStatuses: [
      "Pending",
      "Assigned",
      "Resolved",
      "Completed",
      "InProgress",
      "Disapproved",
      "Cancelled",
    ],
  },
  {
    name: "Order History",
    href: "OrderHistory",
    activeStatuses: [
      "Pending",
      "Assigned",
      "Resolved",
      "Disapproved",
      "Completed",
      "InProgress",
      "Cancelled",
    ],
  },
  {
    name: "Resolution Details",
    href: "ResolutionDetails",
    activeStatuses: ["Resolved", "Completed"],
  },
];

const cancellableStatuses = ["Pending", "Assigned", "InProgress"];
const assignableStatuses = ["Pending", "Assigned", "InProgress"];

export default function ViewRATServiceOrderContainer({
  open,
  setOpen,
  id,
}: {
  id?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const [orderTab, setOrderTab] = useState("OrderDetails");
  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: id || searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !(id || searchParams.id),
  });

  const order = data?.serviceOrder;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      hidePadding
      size="4xl"
      title="Readiness Assessment Test Information"
      description="Details of this readiness assessment test are shown below"
      renderActions={() => (
        <>
          {assignableStatuses.includes(order?.status) && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(
                dispatchModal(order?._id, "assign"),
              )}
            >
              {order?.status === "Pending" ? "Assign" : "Reassign"}
            </button>
          )}
          {cancellableStatuses.includes(order?.status) && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(dispatchModal(order?._id, "cancel"))}
            >
              Cancel
            </button>
          )}
          {order?.status === "Resolved" && (
            <>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(order?._id, "approve"),
                )}
              >
                Approve
              </button>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(order?._id, "disapprove"),
                )}
              >
                Disapprove
              </button>
            </>
          )}
        </>
      )}
    >
      {order?._id && (
        <>
          {[
            "Assigned",
            "InProgress",
            "Resolved",
            "Completed",
            "Disapproved",
          ].includes(order.status) && (
            <Tabs
              tabs={orderTabs}
              onTabChanged={setOrderTab}
              status={order.status}
            />
          )}
          <div className="flex-1 w-full max-h-[65vh] overflow-y-auto sm:p-6">
            {orderTab === "OrderDetails" && (
              <RATServiceOrderDetails serviceOrder={order} />
            )}
            {orderTab === "OrderHistory" && (
              <ServiceOrderHistoryView history={order.history} />
            )}
            {orderTab === "ResolutionDetails" && (
              <RATServiceOrderResolutionDetails
                notes={order.resolutionNotes}
                result={order.result}
              />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
